import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';
import { RiCheckboxCircleFill } from 'react-icons/ri';

import styles from './console.module.scss';

import Spinner from '@/components/ui/Spinner';
import useCalibrationCommand from '@/hooks/command/mavlink/useCalibrationCommand';
import EventEmitter from '@/libs/EventEmitter';
import { ModalService as modal } from '@/libs/Modal';
import { getCSSVariable } from '@/utils/theme';

const cx = classNames.bind(styles);

const Panel = ({ robot }) => {
  const [isStarted, setStarted] = useState(false);
  const [isSuccess, setSuccess] = useState();
  const subscribeToken = useRef();
  const progressBarRef = useRef();
  const progressTextRef = useRef();
  const calibrationCommand = useCalibrationCommand();

  useMountEffect(() => {
    return () => {
      calibrationCommand.compassCancel(robot);
      unsubscribe();
    };
  });

  const unsubscribe = () => {
    if (subscribeToken.current) {
      EventEmitter.unsubscribe(subscribeToken.current);
      subscribeToken.current = null;
    }
  };

  const doStart = async () => {
    progressBarRef.current.style.setProperty('width', '0%');
    progressTextRef.current.innerText = '0%';
    setStarted(true);

    await new Promise((resolve) => {
      subscribeToken.current = EventEmitter.subscribe(`${robot.id}/telemetry/commandAck`, (data) => {
        // 42426: MAV_CMD_DO_CANCEL_MAG_CAL
        if (data.command === 42426 && data.result === 0) {
          unsubscribe();
          resolve();
        }
      });
      calibrationCommand.compassCancel(robot);
    });

    await new Promise((resolve) => {
      subscribeToken.current = EventEmitter.subscribe(`${robot.id}/telemetry/commandAck`, (data) => {
        // 42424: MAV_CMD_DO_START_MAG_CAL
        if (data.command === 42424 && data.result === 0) {
          unsubscribe();
          resolve();
        }
      });
      calibrationCommand.compassStart(robot);
    });

    await new Promise((resolve) => {
      subscribeToken.current = EventEmitter.subscribe(`${robot.id}/telemetry/magCalProgress`, (data) => {
        let percentage = `${data.completionPct}%`;
        if (data.completionPct === 99) {
          percentage = '100%';
        }

        progressBarRef.current.style.setProperty('width', percentage);
        progressTextRef.current.innerText = percentage;

        if (data.completionPct === 99) {
          unsubscribe();
          resolve();
        }
      });
    });

    await new Promise((resolve) => {
      subscribeToken.current = EventEmitter.subscribe(`${robot.id}/telemetry/magCalReport`, (data) => {
        unsubscribe();
        setSuccess(data.calStatus === 4);
        resolve();
      });
    });
  };

  const doReboot = () => {
    calibrationCommand.reboot(robot);
    modal.hide();
  };

  return (
    <div className={cx('container')}>
      <div className={cx('message')}>Rotate your vehicle through a number of positions.</div>
      {!isStarted && (
        <button type="button" className={cx('button')} onClick={doStart}>
          Start
        </button>
      )}
      {isStarted && isSuccess === undefined && (
        <div className={cx('status')}>
          <Spinner size={20} />
          <div className={cx('text')}>Processing...</div>
        </div>
      )}
      <div className={cx(['progress', { show: isStarted && isSuccess === undefined }])}>
        <div ref={progressBarRef} className={cx('bar')} />
        <div ref={progressTextRef} className={cx('text')} />
      </div>
      {isSuccess && (
        <>
          <div className={cx('status')}>
            <RiCheckboxCircleFill size={20} color={getCSSVariable('--tint-color')} className={cx('message')} />
            <div className={cx('text')}>Done!</div>
          </div>
          <div className={cx('message')}>Reboot required to refresh magnetometer calibration parameters.</div>
          <button type="button" className={cx('button')} onClick={doReboot}>
            Reboot
          </button>
        </>
      )}
      {isSuccess === false && (
        <div className={cx('status')}>
          <RiCheckboxCircleFill size={20} color="red" className={cx('message')} />
          <div className={cx('text')}>Failure!</div>
        </div>
      )}
    </div>
  );
};

export default Panel;
